import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CanvasArtwork } from "../data/canvasArtworks";
import { artworkPagePath } from "../lib/artworkPaths";
import { isCanvasSold } from "../lib/canvasAvailability";
import { useCanvasArtwork } from "../hooks/useTranslatedArtwork";
import { ArtworkCardGallery } from "./ArtworkImageGallery";
import { ArtworkLightboxActions } from "./ArtworkLightboxActions";
import { CanvasInvestmentPrice } from "./CanvasInvestmentPrice";
import { CanvasArtworkTitleRow } from "./CanvasArtworkTitleRow";

export function FeaturedCanvasCard({
  work,
  onClick,
}: {
  work: CanvasArtwork;
  onClick: () => void;
}) {
  const translated = useCanvasArtwork(work);
  const sold = isCanvasSold(work);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group art-card w-full animate-fade-in border-0 p-0 text-start"
    >
      <ArtworkCardGallery
        images={translated.galleryImages}
        aspectClass="aspect-[3/4]"
        imgClassName="transition-transform duration-700 group-hover:scale-105"
      />
      <div className="p-3 sm:p-4">
        <CanvasArtworkTitleRow
          title={translated.name}
          sold={sold}
          className="font-display text-base font-medium text-canvas-950 sm:text-lg"
        />
        <p className="mt-1 text-xs text-canvas-500">{translated.medium}</p>
        <p className="mt-1 text-xs text-canvas-500">
          {translated.dimensionsFormatted}
        </p>
      </div>
    </button>
  );
}

export function CanvasGalleryCard({
  work,
  onClick,
  to,
  style,
  className = "mb-5",
}: {
  work: CanvasArtwork;
  onClick?: () => void;
  to?: string;
  style?: React.CSSProperties;
  className?: string;
}) {
  const { t } = useTranslation();
  const translated = useCanvasArtwork(work);
  const sold = isCanvasSold(work);
  const cardClass = `group art-card block w-full break-inside-avoid text-start animate-fade-in ${className}`;

  const content = (
    <>
      <ArtworkCardGallery
        images={translated.galleryImages}
        aspectClass="aspect-[4/5]"
        imgClassName="transition-transform duration-700 group-hover:scale-[1.03]"
      />
      <div className="p-5">
        <CanvasArtworkTitleRow title={translated.name} sold={sold} />
        <p className="mt-1 text-xs text-canvas-500">{translated.medium}</p>
        <p className="mt-1 text-xs text-canvas-500">
          {translated.dimensionsFormatted}
        </p>
        {sold ? (
          <p className="mt-2 text-xs leading-relaxed text-canvas-500">
            {t("canvas.sold.cardNote")}
          </p>
        ) : (
          <p className="mt-1">
            <CanvasInvestmentPrice
              price={translated.investmentPrice}
              layout="stacked"
              size="sm"
            />
          </p>
        )}
        {translated.description && (
          <p className="mt-2 line-clamp-2 text-sm text-canvas-600">
            {translated.description}
          </p>
        )}
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cardClass} style={style}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${cardClass} border-0 p-0`}
      style={style}
    >
      {content}
    </button>
  );
}

export function CanvasLightboxContent({
  work,
  exploreGalleryHref,
}: {
  work: CanvasArtwork;
  exploreGalleryHref?: string;
}) {
  const { t } = useTranslation();
  const translated = useCanvasArtwork(work);
  const sold = isCanvasSold(work);

  return (
    <>
      <p className="text-xs font-medium uppercase tracking-[0.15em] text-accent">
        {translated.collectionLabel}
      </p>
      <CanvasArtworkTitleRow
        title={translated.title}
        sold={sold}
        as="h2"
        className="mt-2 font-display text-xl font-semibold text-canvas-950 sm:text-2xl md:text-3xl"
      />
      {translated.description && (
        <p className="mt-3 text-sm leading-relaxed text-canvas-600 sm:mt-4">
          {translated.description}
        </p>
      )}
      <p className="mt-3 text-sm text-canvas-500">
        {translated.dimensionsFormatted}
      </p>
      {sold ? (
        <p className="mt-2 text-xs leading-relaxed text-canvas-500">
          {t("canvas.sold.cardNote")}
        </p>
      ) : (
        <p className="mt-1">
          <CanvasInvestmentPrice price={translated.investmentPrice} size="md" />
        </p>
      )}
      <ArtworkLightboxActions
        artworkPageHref={artworkPagePath("canvas", work.id)}
        exploreGalleryHref={exploreGalleryHref}
        share={{
          medium: "canvas",
          id: work.id,
          title: translated.title,
          text: translated.description,
        }}
        inquire={
          sold
            ? undefined
            : {
                medium: "canvas",
                id: work.id,
                name: translated.name,
              }
        }
      />
    </>
  );
}
